import { RefObject, useLayoutEffect } from 'react'
import styles from './BriefShell.module.css'

/** Position every visible note relative to its section, leaving room for earlier notes. */
export const usePinnedNoteLayout = (
  containerRef: RefObject<HTMLDivElement>,
  active: boolean,
  content: unknown,
  viewMode: string,
  layoutVersion: number
): void => {
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || !active) return

    const updateLayout = () => {
      const notes = Array.from(container.querySelectorAll<HTMLElement>(`.${styles.absoluteNote}`))
      notes.sort((first, second) => {
        const firstSection = first.closest(`.${styles.sectionWithNote}`)
        const secondSection = second.closest(`.${styles.sectionWithNote}`)
        return (firstSection?.getBoundingClientRect().top ?? 0) - (secondSection?.getBoundingClientRect().top ?? 0)
      })

      const containerTop = container.getBoundingClientRect().top
      let lowestOccupiedY = 0
      notes.forEach(note => {
        const section = note.closest(`.${styles.sectionWithNote}`)
        if (!section) return
        const sectionTop = section.getBoundingClientRect().top - containerTop
        const targetY = Math.max(sectionTop, lowestOccupiedY)
        note.style.transform = `translate3d(0, ${targetY - sectionTop}px, 0)`
        lowestOccupiedY = targetY + note.offsetHeight + 24
      })
    }

    // Measure before paint, then through the 350ms accordion/layout transition.
    updateLayout()
    let rafId: number
    const started = performance.now()
    const frame = (now: number) => {
      updateLayout()
      if (now - started < 400) rafId = requestAnimationFrame(frame)
    }
    rafId = requestAnimationFrame(frame)

    window.addEventListener('resize', updateLayout)
    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateLayout)
      observer.observe(container)
      container
        .querySelectorAll<HTMLElement>(`.${styles.absoluteNote}, .${styles.sectionWithNote}`)
        .forEach(element => {
          observer?.observe(element)
        })
    }

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateLayout)
      observer?.disconnect()
    }
  }, [containerRef, active, content, viewMode, layoutVersion])
}
